<!-- src/views/pages/superadmin/packages/packages-grid.vue -->
<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import * as bootstrap from "bootstrap";
import planService from "@/services/super-admin/planService";
import { toast } from "@/utils/toast";
import { usePlanStore } from "@/stores/planStore";

const title = ref("Packages");
const text = ref("Superadmin");
const text1 = ref("Packages Grid");

const planStore = usePlanStore();

// local UI state
const billingPeriod = ref("monthly"); // 'monthly' | 'yearly'
const currentSort = ref("Recently Added");
const selectedPlanName = ref("All");

const editPlan = ref(null);
const deletePlan = ref(null);

// toggle header collapse
const toggleHeader = () => {
  document.getElementById("collapse-header").classList.toggle("active");
  document.body.classList.toggle("header-collapse");
};

// helpers
const currencySymbol = (code) => {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "KES":
      return "Ksh";
    default:
      return code + " ";
  }
};

// map module keys to human labels (same mapping used in modal)
const MODULE_LABELS = {
  // Projects
  projects: "Projects",
  tasks: "Tasks",
  task_board: "Task Board",

  // CRM
  crm_contacts: "Contacts",
  crm_clients: "Clients",
  crm_deals: "Deals",
  crm_leads: "Leads",
  crm_pipeline: "Pipeline",
  crm_analytics: "Analytics",
  crm_activities: "Activities",

  // HRM
  hrm_employee: "Employee",
  hrm_tickets: "Tickets",
  hrm_holidays: "Holidays",
  hrm_attendance: "Attendance",
  hrm_performance: "Performance",
  hrm_training: "Training",
  hrm_promotion: "Promotion",
  hrm_resignation: "Resignation",
  hrm_termination: "Termination",

  // Recruitment
  recruit_jobs: "Jobs",
  recruit_candidates: "Candidates",
  recruit_referrals: "Referrals",

  // Finance & Accounts
  fa_sales: "Sales",
  fa_accounting: "Accounting",
  fa_payroll: "Payroll",

  // Administration
  admin_assets: "Assets",
  admin_help_support: "Help & Supports",
  admin_reports: "Reports",
  admin_settings: "Settings",
};

// const featureLabelsForPlan = (plan) => {
//   if (!Array.isArray(plan.modules)) return [];
//   return plan.modules
//     .map((key) => MODULE_LABELS[key] || key)
//     .slice(0, 6); // limit shown modules
// };

const featureLabelsForPlan = (plan) => {
  if (!Array.isArray(plan.modules)) return [];
  return plan.modules.map((key) => MODULE_LABELS[key] || key);
};






// stats
const totalPlans = computed(() => planStore.plans.length);

const activePlans = computed(
  () => planStore.plans.filter((p) => p.status === "active").length
);

const inactivePlans = computed(
  () => planStore.plans.filter((p) => p.status === "inactive").length
);

const planTypesCount = computed(() => {
  const set = new Set(planStore.plans.map((p) => p.billing_period));
  return set.size;
});

// dropdown options
const planNameOptions = computed(() => {
  const names = Array.from(new Set(planStore.plans.map((p) => p.name))).sort();
  return ["All", ...names];
});

// sorting + filtering
const sortedFilteredPlans = computed(() => {
  let plans = planStore.plans.filter(
    (p) => p.billing_period === billingPeriod.value
  );

  if (selectedPlanName.value !== "All") {
    plans = plans.filter((p) => p.name === selectedPlanName.value);
  }

  switch (currentSort.value) {
    case "Ascending":
      plans.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "Descending":
      plans.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case "Recently Added":
      plans.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      break;
    case "Last Month": {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      plans = plans.filter((p) => {
        const created = new Date(p.created_at);
        return created >= start && created <= end;
      });
      break;
    }
    case "Last 7 Days": {
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      plans = plans.filter(
        (p) => new Date(p.created_at) >= lastWeek
      );
      break;
    }
  }

  // position (if set) can be used as secondary sort
  plans.sort((a, b) => (a.position || 999) - (b.position || 999));

  return plans;
});

// open / close modals
const clearEditPlan = () => {
  editPlan.value = null;
  deletePlan.value = null;
};

const openAddModal = () => {
  clearEditPlan();
  nextTick(() => {
    const modal = new bootstrap.Modal(
      document.getElementById("add_plans")
    );
    modal.show();
  });
};

const openEditModal = (plan) => {
  clearEditPlan();
  nextTick(() => {
    editPlan.value = { ...plan };
    nextTick(() => {
      const modal = new bootstrap.Modal(
        document.getElementById("edit_plans")
      );
      modal.show();
    });
  });
};

const openDeleteModal = (plan) => {
  deletePlan.value = plan;
  nextTick(() => {
    const modal = new bootstrap.Modal(
      document.getElementById("delete_modal")
    );
    modal.show();
  });
};

const onPlansUpdated = async () => {
  await planStore.fetchPlans();
};

const onModalClosed = () => {
  clearEditPlan();
};

const handlePeriodToggle = (event) => {
  billingPeriod.value = event.target.checked ? "yearly" : "monthly";
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const exportPlans = async (format) => {
  try {
    const res = await planService.export(
      {
        billing_period: billingPeriod.value,
      },
      format
    );
    downloadBlob(
      res.data,
      format === "pdf" ? "plans-export.pdf" : "plans-export.xlsx"
    );
  } catch (error) {
    toast(error?.response?.data?.message || "Failed to export plans", "error");
  }
};

onMounted(async () => {
  if (!planStore.plans.length) {
    await planStore.fetchPlans();
  }
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper dq-platform">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="me-2 mb-2">
            <div class="d-flex align-items-center border bg-white rounded p-1 me-2">
              <router-link
                to="/super-admin/packages"
                class="btn btn-icon btn-sm me-1"
              >
                <i class="ti ti-list-tree"></i>
              </router-link>
              <router-link
                to="/super-admin/packages-grid"
                class="btn btn-icon btn-sm bg-primary text-white active"
              >
                <i class="ti ti-layout-grid"></i>
              </router-link>
            </div>
          </div>
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a href="javascript:void(0);" class="dropdown-item rounded-1" @click.prevent="exportPlans('pdf')">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item rounded-1" @click.prevent="exportPlans('excel')">
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAddModal"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Plan
            </a>
          </div>
          <div class="ms-2 head-icons">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Stats row -->
      <div class="row dq-stat-grid">
        <!-- Total Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-body d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">Total Plans</p>
                  <h4>{{ totalPlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-primary flex-shrink-0">
                  <i class="ti ti-box fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- Active Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-body d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">Active Plans</p>
                  <h4>{{ activePlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-success flex-shrink-0">
                  <i class="ti ti-activity-heartbeat fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- Inactive Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-body d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">Inactive Plans</p>
                  <h4>{{ inactivePlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-danger flex-shrink-0">
                  <i class="ti ti-player-pause fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- No of Plan Types -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div class="card-body d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">No of Plan Types</p>
                  <h4>{{ planTypesCount }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-skyblue flex-shrink-0">
                  <i class="ti ti-mask fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Stats row -->

      <!-- Filters card -->
      <div class="card">
        <div class="card-body p-3">
          <div class="d-flex align-items-center justify-content-between">
            <h5>Plans List</h5>
            <div
              class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
            >
              <!-- Plan Name filter -->
              <div class="dropdown me-3">
                <a
                  href="javascript:void(0);"
                  class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  Select Plan
                </a>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li
                    v-for="name in planNameOptions"
                    :key="name"
                  >
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="selectedPlanName = name"
                    >
                      {{ name }}
                    </a>
                  </li>
                </ul>
              </div>

              <!-- Sort dropdown -->
              <div class="dropdown">
                <a
                  href="javascript:void(0);"
                  class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  Sort By : {{ currentSort }}
                </a>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="currentSort = 'Recently Added'"
                    >
                      Recently Added
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="currentSort = 'Ascending'"
                    >
                      Ascending
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="currentSort = 'Descending'"
                    >
                      Desending
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="currentSort = 'Last Month'"
                    >
                      Last Month
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click="currentSort = 'Last 7 Days'"
                    >
                      Last 7 Days
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing grid -->
      <div class="card">
        <div class="card-body">
          <!-- monthly / yearly switch -->
          <div class="d-flex justify-content-center align-items-center mb-4">
            <p class="mb-0 me-2">Monthly</p>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="flexSwitchCheckDefault"
                :checked="billingPeriod === 'yearly'"
                @change="handlePeriodToggle"
              />
            </div>
            <p class="mb-0">Yearly</p>
          </div>

          <!-- Loading state -->
          <div
            v-if="planStore.loading"
            class="d-flex justify-content-center align-items-center py-5"
          >
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!sortedFilteredPlans.length"
            class="text-center py-5"
          >
            <h5 class="mb-2">No plans found</h5>
            <p class="text-muted mb-3">
              Try changing filters or add a new plan.
            </p>
            <button class="btn btn-primary" @click="openAddModal">
              <i class="ti ti-circle-plus me-2"></i>Add Plan
            </button>
          </div>

          <!-- Plans cards -->
          <div
            v-else
            class="row justify-content-center"
          >
            <div
              v-for="plan in sortedFilteredPlans"
              :key="plan.id"
              class="col-lg-3 col-md-6 col-sm-12 d-flex"
            >
              <div class="card flex-fill">
                <div class="card-body">
                  <div class="card mb-3">
                    <div class="card-body d-flex justify-content-between align-items-start">
                      <div>
                        <h4 class="mb-1">{{ plan.name }}</h4>
                        <h1 class="mb-0">
                          {{ currencySymbol(plan.currency) }}{{ Number(plan.price).toFixed(2) }}
                          <span class="fs-14 fw-normal text-gray">
                            /{{ plan.billing_period }}
                          </span>
                        </h1>
                      </div>
                      <div class="text-end">
                        <span
                          v-if="plan.is_recommended"
                          class="badge badge-success mb-2"
                        >
                          Recommended
                        </span>
                        <div class="dropdown">
                          <a
                            href="javascript:void(0);"
                            class="text-muted"
                            data-bs-toggle="dropdown"
                          >
                            <i class="ti ti-dots-vertical"></i>
                          </a>
                          <ul class="dropdown-menu dropdown-menu-end p-2">
                            <li>
                              <a
                                href="javascript:void(0);"
                                class="dropdown-item"
                                @click="openEditModal(plan)"
                              >
                                <i class="ti ti-edit me-1"></i>Edit
                              </a>
                            </li>
                            <li>
                              <a
                                href="javascript:void(0);"
                                class="dropdown-item text-danger"
                                @click="openDeleteModal(plan)"
                              >
                                <i class="ti ti-trash me-1"></i>Delete
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="pricing-content rounded bg-light mb-3">
                    <div class="price-hdr">
                      <h6 class="fs-14 fw-medium text-gray w-100">
                        Features Includes
                      </h6>
                    </div>
                    <div class="pt-2">
                      <!-- numeric limits -->
                      <span
                        v-if="plan.max_customers"
                        class="text-dark d-flex align-items-center mb-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        Max {{ plan.max_customers }} Customers
                      </span>
                      <span
                        v-if="plan.max_invoices"
                        class="text-dark d-flex align-items-center mb-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        Max {{ plan.max_invoices }} Invoices
                      </span>
                      <span
                        v-if="plan.max_products"
                        class="text-dark d-flex align-items-center mb-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        Max {{ plan.max_products }} Products
                      </span>
                      <span
                        v-if="plan.max_suppliers"
                        class="text-dark d-flex align-items-center mb-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        Max {{ plan.max_suppliers }} Suppliers
                      </span>

                      <!-- modules -->
                      <span
                        v-for="feature in featureLabelsForPlan(plan)"
                        :key="feature"
                        class="text-dark d-flex align-items-center mb-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        {{ feature }}
                      </span>

                      <span
                        v-if="!featureLabelsForPlan(plan).length &&
                          !plan.max_customers &&
                          !plan.max_invoices &&
                          !plan.max_products &&
                          !plan.max_suppliers"
                        class="text-muted d-flex align-items-center"
                      >
                        <i class="ti ti-circle-x-filled text-danger me-2"></i>
                        No specific features configured
                      </span>

                      <span
                        v-if="plan.allow_trial && plan.trial_days"
                        class="text-dark d-flex align-items-center mt-2"
                      >
                        <i class="ti ti-discount-check-filled text-success me-2"></i>
                        {{ plan.trial_days }} days free trial
                      </span>
                    </div>
                  </div>

                  <a href="javascript:void(0);" class="btn btn-dark w-100">
                    Choose Plan
                  </a>
                </div>
              </div>
            </div>
          </div>
          <!-- /Plans cards -->
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->

  <!-- Modal component, wired to grid -->
  <packages-modal
    :plan="editPlan"
    :delete-plan="deletePlan"
    @plan-updated="onPlansUpdated"
    @modal-closed="onModalClosed"
  />
</template>


